import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { Alert } from "@models/alerts/alert";
import { AlertRecord } from "@models/alerts/alert-record";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Tag } from "@models/common/tag";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterOption } from "@models/grid/grid-filter";
import { ExportEntityListModalComponent } from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import { excelReportTitleStyles, excelTitleForReport } from "@shared/utils/excel-title-for-report";
import { ColDef, CsvExportParams, ExcelExportParams, ExcelRow, ExcelStyle, GridApi, ProcessCellForExportParams, ProcessHeaderForExportParams } from "ag-grid-community";
import { deepClone } from "fast-json-patch";
import { take } from "rxjs";
import { ModalFormsService } from "src/app/forms/services/modal-forms.service";

Injectable()
export class AlertsExportService {
  modalFormsService = inject(ModalFormsService);

  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  exportAlertsList(
    gridApi: GridApi,
    exportType: ExportTypeEnum,
    isBulkExport: boolean,
    colDefs: ColDef[],
    checkedInboxAlerts: AlertRecord[],
    totalRows: number,
    sheetName: string
  ): void {
    const modalRef = this.modalFormsService.openModal(ExportEntityListModalComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ExportEntityListModalComponent;

    componentInstance.title = 'Export Alerts';
    componentInstance.subTitle = 'alerts selected';
    componentInstance.colDefs = deepClone(colDefs) as ColDef[];
    componentInstance.numberOfCompanies = isBulkExport ? checkedInboxAlerts.length : totalRows;
    componentInstance.exportType = exportType;
    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        const numberColumnForExport = columnForExport.length - 1;

        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          const exportParamsXls = this.exportParamsXls(numberColumnForExport, sheetName);
          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params });
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          gridApi.exportDataAsCsv(params);
        }
      });
  }

  getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) => {
    return excelTitleForReport(numberColumnForExport, 'Alerts Report');
  }

  formatDate(date: Date): string {
    return formatDate(date, 'yyyy-MM-dd HH:mm:ss', 'en-US');
  }

  exportParams(sheetName: string) {
    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const alertData = params.node?.data as Alert;

        if (params.column.getColId() === 'createdOn') {
          return formatDate(alertData.createdOn ?? '', 'dd MMM yyyy HH:mm', 'en-US');
        } else if (params.column.getColId() === 'type' || params.column.getColId() === 'status' || params.column.getColId() === 'resolvedStatus') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          const options: FilterOption[] = params.column.getColDef()?.filterParams?.filterOptions ?? [];
          return options.find(o => o.value == value)?.label ?? '';
        } else if (params.column.getColId() === 'tags') {
          return (value as Tag[]).map((tag: Tag) => tag.name).join(', ');
        } else if (params.column.getColId() === 'needAttention') {
          return value ? 'Yes' : 'No';
        } else if (params.column.getColId() === 'isRead') {
          return value ? 'Yes' : 'No';
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      sheetName,
      fileName: `${ sheetName }  ${ formatDate(new Date(), 'dd-MM-yyyy', 'en-US') }`
    }
  }

  exportParamsXls(numberColumnForExport: number, sheetName: string): ExcelExportParams {
    return {
      ...this.exportParams(sheetName),
      prependContent: this.getExcelTitleRows(numberColumnForExport)
    } as ExcelExportParams;
  }
}